"use client";

import * as React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
} from "../../components/ui/drawer";
import { EmptyState } from "../../components/ui/empty";
import { Icon } from "../../components/ui/icon";
import {
  isPickerIconName,
  PICKER_ICON_GROUPS,
  pickerIconLabel,
  type GlyphName,
  type IconName,
} from "../../components/ui/icon-registry";
import { InputGroup, InputGroupInput } from "../../components/ui/input-group";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { Text } from "../../components/ui/text";
import { useIsMobile } from "../../hooks/use-media-query";
import {
  Tooltip,
  TooltipPopup,
  TooltipSideProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { colorLabel, type Color, type NoColor } from "../../lib/colors";
import { useThemeMode } from "../../components/theme-provider";
import { cva, type VariantProps } from "../../lib/cva";
import { WASH_HOVER, WASH_SELECTED } from "../../components/ui/box-classes";
import { cn } from "../../lib/utils";
import { ColorPalette, ColorSwatch } from "../color-swatch";
import type { Omittable } from "../../lib/props";

// The grid offers the picker set and nothing else: the curated Material
// Symbols glyphs, in their groups. App-surface icons never appear here, which
// is what keeps a workflow from wearing the map's or the settings' glyph. The
// one app-surface name that can reach the grid is a caller's `defaultIcon`
// (an entity's kind glyph), hoisted to the front so the default is pickable.
const PICKER_SECTIONS: readonly {
  readonly id: string;
  readonly label: string;
  readonly names: readonly GlyphName[];
}[] = PICKER_ICON_GROUPS;

/** What a cell says: a picker glyph reads by its words ("account tree"), an
 *  app-surface default by its registry name. */
function glyphLabel(name: GlyphName): string {
  return isPickerIconName(name) ? pickerIconLabel(name) : name;
}

type GridSection = {
  id: string;
  /** Null for the hoisted default — a lone cell needs no heading. */
  label: string | null;
  names: GlyphName[];
};

function sectionsFor(
  defaultIcon: GlyphName | undefined,
  search: string,
): GridSection[] {
  const query = search.trim().toLowerCase();
  const lead: GridSection[] =
    defaultIcon !== undefined &&
    (query === "" || glyphLabel(defaultIcon).includes(query))
      ? [{ id: "default", label: null, names: [defaultIcon] }]
      : [];
  const groups = PICKER_SECTIONS.flatMap((section): GridSection[] => {
    const names = section.names.filter(
      (name) =>
        name !== defaultIcon &&
        (query === "" ||
          glyphLabel(name).includes(query) ||
          section.label.toLowerCase().includes(query)),
    );
    return names.length ? [{ id: section.id, label: section.label, names }] : [];
  });
  return [...lead, ...groups];
}

// The grid is a fixed number of tracks, and the popup's width is DERIVED from
// it — not typed beside it. A wrapping row has to survive its container's
// border and subpixel rounding, which is how a row sized to fit an exact
// swatch count broke 9 + 2 and the glyph rows landed on whatever count fit; a
// track count cannot drift that way, and nothing downstream can disagree with
// a width the grid itself sets.
//
// The cell is 32 with a 4px gutter and the glyph stays 20, which is what makes
// a glyph read as a glyph rather than as texture: at 28/2 the same 20px mark
// has 4px of air and ten of its neighbours in view, and the field flattens.
// Eight columns, six whole rows.
export const GRID_COLUMNS = 8;
const GRID_VISIBLE_ROWS = 6;
export const CELL = 32;
const CELL_GAP = 4; // the `xs` token
const GRID_WIDTH = GRID_COLUMNS * CELL + (GRID_COLUMNS - 1) * CELL_GAP;
const GRID_HEIGHT =
  GRID_VISIBLE_ROWS * CELL + (GRID_VISIBLE_ROWS - 1) * CELL_GAP;

// The palette is a grid for the same reason. Six tracks means the twelve
// curated hues fill two rows exactly; the optional "no color" swatch starts a
// third.
const PALETTE_COLUMNS = 6;

// On a phone the picker is a bottom sheet, and the sheet sets the width the
// popup's grid used to: the tracks flex between a 44px touch floor and the
// sheet's inset edge, so every row fills the width and a glyph lands under a
// thumb. The 32/4 popup rhythm is a pointer rhythm and does not travel.
const SHEET_GRID_COLUMNS = "grid-cols-[repeat(auto-fill,minmax(2.75rem,1fr))]";

// The search line and the colour well hold the 28px control rung on EVERY
// viewport. The rungs step up on touch by default, which is right for a form
// field and wrong inside a popup that has one rung for its own chrome.
const ROW_CONTROL = "h-7 min-w-7";

export const visualPickerTriggerVariants = cva(
  // A pressable control, not a static tile: it carries the Button focus-ring
  // grammar and hover/open states so it reads as "press me to change this".
  // Disabled keeps the fill and the glyph under the library's one disabled
  // state (64% on the whole control) — a read-only surface still has to SHOW
  // the appearance it is not letting you edit.
  "flex cursor-pointer items-center justify-center rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-default disabled:opacity-64",
  {
    compoundVariants: [
      { class: "bg-c-soft", tint: true, variant: "soft" },
      { class: "bg-c", tint: true, variant: "solid" },
      // Neutral = the muted well (the same fill icon wells use — dark
      // `secondary` equals the card fill and disappears there), stepping to
      // the `accent` neutral-hover surface on hover and while the popup is
      // open. `outline` keeps its own chrome whether or not a color is set;
      // only the glyph ink goes neutral there.
      {
        class: "bg-muted hover:bg-accent data-popup-open:bg-accent",
        tint: false,
        variant: ["soft", "solid"],
      },
    ],
    defaultVariants: { size: "default", tint: false, variant: "soft" },
    variants: {
      size: {
        sm: "h-8 w-8",
        default: "p-2",
      },
      // The chosen color's fill strength; without a tint (no color section,
      // or an explicit "no color" pick) the soft and solid tiles stay
      // neutral either way, so their fill classes live in `compoundVariants`.
      // `outline` carries the color in the glyph alone: an input hairline
      // over NO fill of its own, so the
      // tile shows the surface it sits on (a settings card, a dialog body)
      // and reads as part of that section rather than a hole cut down to
      // `background`, which in dark mode sits below the card. The hover wash
      // then composites onto that same host fill.
      variant: {
        soft: "",
        solid: "",
        outline:
          "border border-input bg-transparent hover:bg-wash-hover data-popup-open:bg-wash-active",
      },
      tint: { false: "", true: "" },
    },
  },
);

// The glyph/monogram ink twin of the trigger fill: colored ink on the soft
// wash and on the outline's plain fill, contrast ink on the solid fill,
// muted when neutral.
export const visualPickerTriggerInkVariants = cva("", {
  compoundVariants: [
    { class: "text-c", tint: true, variant: ["soft", "outline"] },
    { class: "text-c-foreground", tint: true, variant: "solid" },
  ],
  defaultVariants: { tint: false, variant: "soft" },
  variants: {
    variant: { soft: "", solid: "", outline: "" },
    tint: { false: "text-muted-foreground", true: "" },
  },
});

// Monogram letter per trigger size — a touch under the glyph box so wide
// letters don't crowd the tile edge (matches the glyph tiers lg/xl).
export const visualPickerMonogramVariants = cva(
  "leading-none font-semibold select-none",
  {
    defaultVariants: { size: "default" },
    variants: {
      size: {
        sm: "text-[13px]",
        default: "text-[15px]",
      },
    },
  },
);

export type VisualPickerSize = NonNullable<
  VariantProps<typeof visualPickerTriggerVariants>["size"]
>;
export type VisualPickerVariant = NonNullable<
  VariantProps<typeof visualPickerTriggerVariants>["variant"]
>;

export type VisualPickerAction = Omittable<{
  icon: IconName;
  label: string;
  onClick?: () => void;
  /** Renders the row disabled. An action with no `onClick` is disabled
   *  regardless — an affordance whose backing does not exist yet is shown,
   *  not hidden. */
  disabled?: boolean;
}>;

// `noColor` is a type-level discriminant (mirroring ColorPalette): only
// pickers that opt in ever receive or must handle the NO_COLOR selection.
type VisualPickerColorProps =
  | {
      /** Omit the color pair to drop the Color section entirely. */
      color?: never;
      onColorChange?: never;
      noColor?: never;
    }
  | {
      color: Color;
      onColorChange: (color: Color) => void;
      noColor?: false;
    }
  | {
      color: Color | NoColor;
      onColorChange: (color: Color | NoColor) => void;
      noColor: true;
    };

type VisualPickerIconProps =
  | {
      /** Omit the icon pair to drop the Icon section (search + grid). */
      icon?: never;
      onIconChange?: never;
      monogram?: never;
      defaultIcon?: never;
    }
  | {
      /** The current glyph. A pick is always a picker-set name; a stored
       *  column default or kind fallback may still be an app-surface name,
       *  hence the union. */
      icon: GlyphName;
      /** Reports a picker-set name, or the caller's `defaultIcon` when that
       *  hoisted cell is pressed. */
      onIconChange: (icon: GlyphName) => void;
      monogram?: never;
      /** What the appearance IS before anyone customizes it — the same glyph
       *  a Reset goes back to. The grid leads with it, so the natural pick is
       *  the first cell rather than a scroll through the groups. Monogram mode
       *  has no equivalent: the "Use initial" cell already leads there. */
      defaultIcon?: GlyphName;
    }
  | {
      /** Letter-monogram mode: `icon: null` means "render the initial of
       *  `monogram` instead of a glyph". The icon grid leads with a
       *  "Use initial" cell that selects it by reporting `null`, and the
       *  default trigger renders the letter in the glyph's place. Pass the
       *  live display string (a name field's current value); only its first
       *  character is shown, uppercased, `?` when empty. */
      icon: GlyphName | null;
      onIconChange: (icon: GlyphName | null) => void;
      monogram: string;
      defaultIcon?: never;
    };

export type VisualPickerProps = VisualPickerColorProps &
  VisualPickerIconProps &
  Omittable<{
    /** Footer action rows, rendered behind the popup's footer separator.
     *  Pressing a row closes the popup, then runs the action. */
    actions?: VisualPickerAction[];
    /** Caller-owned trigger element (a ghost button wrapping the entity's own
     *  visual, an avatar, …). Omit it only when the icon section is on — the
     *  classic tinted glyph tile then serves as the default trigger. */
    trigger?: React.ReactElement;
    /** Default tile trigger size. Defaults to 'default'. */
    size?: VisualPickerSize;
    /**
     * Shows the current appearance without letting the popup open — a
     * read-only surface (a view someone cannot manage) still displays the
     * glyph and color it is refusing to edit.
     */
    disabled?: boolean;
    /** Default tile trigger treatment.
     *  - 'soft' (default): tinted background + colored glyph (subtle).
     *  - 'solid': vibrant filled background + contrast glyph (app-tile look).
     *  - 'outline': hairline over the host surface + colored glyph — for a
     *    row whose color already shows elsewhere, so the tile need not shout.
     */
    variant?: VisualPickerVariant;
    /**
     * Hover label on the trigger, doubling as the default trigger's
     * accessible name (the tile is glyph-only). The default trigger names
     * what it edits — "Set avatar" in monogram mode, "Set icon" with the
     * icon section on, "Set color" for a palette-only picker. A caller-owned
     * `trigger` carries its own name, so it gets a tooltip only when this is
     * set. `null` suppresses the default.
     */
    tooltip?: string | null;
    /** Header label. Defaults to "Icon" when the icon section is on, "Color"
     *  when the palette is the whole body. */
    title?: string;
    /**
     * Renders "Reset" in the header. What the default IS belongs to the
     * caller — a team goes back to its monogram, a project type to its type
     * glyph — so the picker only reports the press. Omit it while the value
     * already IS the default: a Reset that would change nothing is noise.
     */
    onReset?: () => void;
    /**
     * Controlled open state. A host that opens the picker from somewhere
     * other than its trigger (a row menu's "Change color") drives this and
     * hears the picker's own dismissals through `onOpenChange`.
     */
    open?: boolean;
    /**
     * Reports the popup's open state. The popup portals outside its anchor,
     * so a host with its own blur/dismiss logic (a transient row that commits
     * on blur) needs this to know a "blur" is really a swatch press.
     */
    onOpenChange?: (open: boolean) => void;
  }>;

export type VisualPickerPresentation = "popover" | "sheet";

export type VisualPickerPanelProps = VisualPickerColorProps &
  VisualPickerIconProps &
  Omittable<{
    /** Footer action rows, rendered behind the panel's footer separator.
     *  Pressing a row reports `onDismiss`, then runs the action. */
    actions?: VisualPickerAction[];
    /** Header label. Defaults to "Icon" when the icon section is on, "Color"
     *  when the palette is the whole body. */
    title?: string;
    /**
     * Renders "Reset" in the header. What the default IS belongs to the
     * caller — a team goes back to its monogram, a project type to its type
     * glyph — so the picker only reports the press. Omit it while the value
     * already IS the default: a Reset that would change nothing is noise.
     */
    onReset?: () => void;
    /** Reports an action-row press so the host popup can close itself before
     *  the action runs. */
    onDismiss?: () => void;
    /** The search input's element ref — a popover host aims `initialFocus`
     *  at it so the grid opens ready to type. */
    searchRef?: React.Ref<HTMLInputElement>;
    /**
     * The surface the panel is seated in. `popover` (the default) is the
     * fixed-width anatomy — header rung, search line with the colour well,
     * the 8-track grid. `sheet` is the phone's bottom drawer: the panel then
     * renders the drawer's own header / panel / footer regions, with the
     * palette laid inline above the search (no nested popup on a phone) and
     * a fluid grid that scrolls in the sheet.
     */
    presentation?: VisualPickerPresentation;
  }>;

/**
 * The picker's body without a popover of its own: the header naming what is
 * being edited (with an optional Reset), then one dense region — the icon
 * search and its fixed grid, with the color palette riding the search line as
 * an outlined well that opens its own swatch popover. A panel with no icon
 * section drops the search and grid and the palette becomes the body;
 * optional action rows sit behind a footer separator.
 *
 * `VisualPicker` seats it inside its own Popover; a host with its own
 * floating surface (a menu submenu, a drawer section) seats it full-bleed
 * instead. The panel keeps no open state — search, the nested palette
 * popover, and the grid tooltip all reset by unmounting with the host popup.
 *
 * Spacing contract (existing tokens only): the header is a 28px control rung
 * in a 32px row inset 12 left / 4 right, so its label and the Reset button's
 * optical edge both land on the popup's 12px line. The body is inset 8 with
 * 8 between its rows — the grid's own 4px glyph inset carries the ink to the
 * same 12px line. Actions are inset 4 with 8px row padding.
 */
export function VisualPickerPanel(props: VisualPickerPanelProps) {
  const {
    actions,
    title,
    onReset,
    onDismiss,
    searchRef,
    presentation = "popover",
  } = props;
  const sheet = presentation === "sheet";
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  // The hovered cell IS the tooltip's anchor AND its text (`data-icon-cell`):
  // one Tooltip root serves the whole grid, because a root per cell is one
  // Base UI root per registry glyph in a single popup.
  const [hoveredCell, setHoveredCell] = React.useState<HTMLElement | null>(
    null,
  );

  const hasColor = props.onColorChange !== undefined;
  const hasIcon = props.onIconChange !== undefined;
  // Narrowed alias for monogram mode — the only member whose onIconChange
  // accepts null, so the "Use initial" cell reports through it.
  const monogramProps = props.monogram !== undefined ? props : null;
  const monogramLetter =
    monogramProps !== null
      ? monogramProps.monogram.trim().charAt(0).toUpperCase() || "?"
      : null;
  const monogramActive = monogramProps !== null && monogramProps.icon === null;

  const defaultIcon = props.defaultIcon;
  // The default leads the grid, hoisted out of its group — and into the pool
  // even though it is an app-surface glyph, because the default must be
  // pickable. Search filters by the words a cell shows and by group label, so
  // a matching default stays first among its matches.
  const sections = React.useMemo(
    () => sectionsFor(defaultIcon, search),
    [search, defaultIcon],
  );
  const firstMatch = sections[0]?.names[0];

  // The grid (and the default tile) tint to the chosen color; without a color
  // section they stay neutral. NO_COLOR carries no data-color binding, so an
  // explicit "no color" pick also renders the neutral treatment.
  // A palette on its own IS the label: a row of swatches under the word "Color"
  // say nothing the swatches do not. The header then renders only when the
  // caller named a title or gave the popup a Reset.
  const headerTitle = title ?? (hasIcon ? "Icon" : undefined);
  const hasHeader = headerTitle !== undefined || onReset !== undefined;

  const tint: Color | undefined =
    hasColor && props.color !== "none" ? props.color : undefined;
  const mode = useThemeMode();
  const swatchLabel = !hasColor
    ? null
    : props.color === "none"
      ? "No color"
      : colorLabel(props.color, mode);

  // The popup lays the palette on six tracks (two full rows in a 300px
  // popup); the sheet has the width for one row, so there the swatches wrap
  // only when the sheet is too narrow for them — never stacked when they
  // need not be.
  const paletteColumns = sheet ? undefined : PALETTE_COLUMNS;
  const palette =
    !hasColor ? null : props.noColor ? (
      <ColorPalette
        noColor
        color={props.color}
        columns={paletteColumns}
        onColorChange={props.onColorChange}
      />
    ) : (
      <ColorPalette
        color={props.color}
        columns={paletteColumns}
        onColorChange={props.onColorChange}
      />
    );

  // Every grid cell reports the same shape, so the monogram cell and a glyph
  // cell share one tooltip and one wash.
  const cellClasses = (selected: boolean) =>
    cn("aspect-square", selected ? WASH_SELECTED : WASH_HOVER);

  const glyphInk = tint ? "text-c" : "text-muted-foreground";

  // The cells are one list whichever surface lays them out: the popup's fixed
  // tracks or the sheet's fluid ones.
  const cells = (
    <>
      {/* Monogram mode leads the grid with the "Use initial" cell — hidden
          while searching, since it matches no glyph name. */}
      {monogramProps !== null && !search ? (
        <Box
          align="center"
          as="button"
          aria-label="Use initial"
          aria-pressed={monogramActive}
          className={cellClasses(monogramActive)}
          data-icon-cell="Use initial"
          justify="center"
          onClick={() => monogramProps.onIconChange(null)}
          rounded="md"
          type="button"
        >
          <Text
            className={cn(
              "leading-none font-semibold select-none",
              sheet ? "text-[15px]" : "text-[13px]",
              glyphInk,
            )}
          >
            {monogramLetter}
          </Text>
        </Box>
      ) : null}
      {sections.map((section) => (
        <React.Fragment key={section.id}>
          {/* A group heading spans the row so the glyphs below it read as
              one family; the hoisted default and a lone result set carry
              none. */}
          {section.label !== null ? (
            <Box
              className="col-span-full"
              paddingTop={section.id === sections[0]?.id ? undefined : "xs"}
              paddingX="xs"
            >
              <Text size="xs" variant="muted" weight="medium">
                {section.label}
              </Text>
            </Box>
          ) : null}
          {section.names.map((name) => {
            const label = glyphLabel(name);
            return (
              <Box
                align="center"
                as="button"
                aria-label={label}
                aria-pressed={name === props.icon}
                className={cellClasses(name === props.icon)}
                data-icon-cell={label}
                data-icon-name={name}
                justify="center"
                key={name}
                onClick={() => props.onIconChange?.(name)}
                rounded="md"
                type="button"
              >
                <Icon className={glyphInk} name={name} size={sheet ? "xl" : "lg"} />
              </Box>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );

  const resetButton = onReset ? (
    <Button onClick={onReset} size={sheet ? "sm" : "xs"} variant="ghost">
      Reset
    </Button>
  ) : null;

  const actionRows =
    actions && actions.length > 0
      ? actions.map((action) => (
          <Button
            className="w-full justify-start"
            disabled={action.disabled ?? !action.onClick}
            key={action.label}
            onClick={() => {
              onDismiss?.();
              action.onClick?.();
            }}
            size="sm"
            variant="ghost"
          >
            <Icon name={action.icon} size="sm" color="muted" />
            {action.label}
          </Button>
        ))
      : null;

  const searchInput = hasIcon ? (
    <InputGroupInput
      onChange={(e) => setSearch(e.target.value)}
      onKeyDown={(event) => {
        // Enter takes the first match: the only keyboard route
        // to a pick, since the grid is a mouse field.
        if (event.key !== "Enter") return;
        if (!firstMatch) return;
        event.preventDefault();
        props.onIconChange?.(firstMatch);
      }}
      placeholder="Search..."
      ref={searchRef}
      size={sheet ? undefined : "sm"}
      value={search}
    />
  ) : null;

  if (sheet) {
    // The drawer names itself even when the popup would carry no header —
    // a palette-only sheet has no visible title, so the name is for
    // assistive tech only. The grid is the sheet's ONE scroller (the panel);
    // the title, palette and search stay pinned above it, and the action
    // rows below it, so neither leaves with a long scroll through the
    // registry. Palette-only has nothing to scroll, so the swatches ARE the
    // panel.
    // The palette row bleeds 8px into the sheet's 24px inset on each side:
    // the twelve hues at the 24/4 rhythm are 332px wide, and the narrowest
    // phones (375) hold that only on a 16px inset — the dots carry no edge
    // of their own, so the wider line still reads inside the sheet.
    //
    // A finger reads a label by holding a swatch, and the hand covers what
    // lies below it — every tooltip in the sheet opens above.
    return (
      <TooltipSideProvider side="top">
        <DrawerHeader>
          <Box
            align="center"
            direction="row"
            justify="between"
            minHeight={28}
          >
            <DrawerTitle visuallyHidden={headerTitle === undefined}>
              {headerTitle ?? "Color"}
            </DrawerTitle>
            {resetButton}
          </Box>
          {hasIcon ? (
            <Box direction="column" gap="md" paddingTop="xs">
              {hasColor ? <Box className="-mx-2">{palette}</Box> : null}
              <InputGroup variant="outline">{searchInput}</InputGroup>
            </Box>
          ) : null}
        </DrawerHeader>
        <DrawerPanel scrollable={hasIcon}>
          {hasColor && !hasIcon ? <Box className="-mx-2">{palette}</Box> : null}
          {hasIcon && sections.length === 0 ? (
            <Box align="center" justify="center" minHeight={120}>
              <EmptyState size="sm" title="No icons found" />
            </Box>
          ) : null}
          {hasIcon && sections.length > 0 ? (
            // The scroll-end gutter: the last rows come clear of the sheet's
            // bottom edge (and a thumb resting there) with room to spare.
            <Box
              className={SHEET_GRID_COLUMNS}
              data-color={tint}
              display="grid"
              gap="xs"
              marginBottom="2xl"
            >
              {cells}
            </Box>
          ) : null}
        </DrawerPanel>
        {actionRows ? (
          <DrawerFooter variant="bare">
            <Box direction="column" gap="2xs">
              {actionRows}
            </Box>
          </DrawerFooter>
        ) : null}
      </TooltipSideProvider>
    );
  }

  return (
    <>
      {hasHeader ? (
        <>
          <Box
            align="center"
            direction="row"
            height={32}
            justify="between"
            paddingLeft="base"
            paddingRight="xs"
          >
            {headerTitle !== undefined ? (
              <Text size="xs" weight="medium">
                {headerTitle}
              </Text>
            ) : null}
            {resetButton}
          </Box>
          <Separator />
        </>
      ) : null}

      <Box direction="column" gap="sm" padding="sm">
        {hasColor && !hasIcon ? palette : null}

        {hasIcon ? (
          <Box align="center" direction="row" gap="sm">
            {/* The framed search well, and nothing in it: a lone field
                directly under a header that already names what is being
                searched does not need a magnifier to announce itself. */}
            <Box fill="flex" shrink>
              <InputGroup
                className={cn(ROW_CONTROL, "**:[input]:h-full")}
                variant="outline"
              >
                {searchInput}
              </InputGroup>
            </Box>

            {hasColor ? (
              <Popover open={paletteOpen} onOpenChange={setPaletteOpen}>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <PopoverTrigger
                        data-slot="visual-picker-color-trigger"
                        render={
                          <Button
                            aria-label={`Color: ${swatchLabel}`}
                            className={ROW_CONTROL}
                            shape="square"
                            size="icon-sm"
                            variant="outline"
                          />
                        }
                      >
                        {/* The value inside its own well: the well is the
                            control, the disc is the datum. An explicit
                            "no color" pick shows the palette's own
                            slash-circle rather than a colorless dot. */}
                        {tint === undefined ? (
                          <Icon color="muted" name="circle-slash" size="md" />
                        ) : (
                          <ColorSwatch
                            color={tint}
                            rounded="full"
                            size="md"
                          />
                        )}
                      </PopoverTrigger>
                    }
                  />
                  <TooltipPopup>{`Color — ${swatchLabel}`}</TooltipPopup>
                </Tooltip>
                {/* Beside the well, so the grid it belongs to stays
                    visible; Base UI flips it when there is no room. */}
                <PopoverPopup align="start" padding="sm" side="right">
                  {palette}
                </PopoverPopup>
              </Popover>
            ) : null}
          </Box>
        ) : null}

        {hasIcon && sections.length === 0 ? (
          <Box
            align="center"
            height={GRID_HEIGHT}
            justify="center"
            width={GRID_WIDTH}
          >
            <EmptyState size="xs" title="No icons found" />
          </Box>
        ) : null}

        {hasIcon && sections.length > 0 ? (
          <Box height={GRID_HEIGHT} width={GRID_WIDTH}>
            <ScrollArea>
              {/* The popup is portalled, so a trigger's `data-color` never
                  reaches it — the grid declares the tint for its own
                  glyphs. */}
              <Box
                columns={GRID_COLUMNS}
                data-color={tint}
                gap="xs"
                onPointerLeave={() => setHoveredCell(null)}
                onPointerOver={(event: React.PointerEvent<HTMLElement>) => {
                  setHoveredCell(
                    (event.target as HTMLElement).closest<HTMLElement>(
                      "[data-icon-cell]",
                    ),
                  );
                }}
                // Scroll events do not bubble, so the wheel gesture is
                // what this can see: without it the bubble rides along
                // with its anchor and can end up outside the viewport it
                // was labelling.
                onWheel={() => setHoveredCell(null)}
                width={GRID_WIDTH}
              >
                {cells}
              </Box>
            </ScrollArea>
          </Box>
        ) : null}
      </Box>

      {actionRows ? (
        <>
          <Separator />
          <Box direction="column" gap="2xs" padding="xs">
            {actionRows}
          </Box>
        </>
      ) : null}

      {/* One root for the whole grid, anchored to whichever cell the pointer
          is over. The names are the searchable registry keys, which is the
          only place they are ever visible. */}
      <Tooltip open={hoveredCell !== null}>
        <TooltipPopup
          anchor={hoveredCell ?? undefined}
          className="whitespace-nowrap"
          side="top"
        >
          {hoveredCell?.dataset.iconCell}
        </TooltipPopup>
      </Tooltip>
    </>
  );
}

// A plain `Omit` over the panel's union collapses its section discriminants
// into one loose shape; distributing keeps each member's pairing intact.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

export type VisualPickerSheetProps = DistributiveOmit<
  VisualPickerPanelProps,
  "presentation" | "onDismiss" | "searchRef"
> &
  Omittable<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The sheet's trigger, when it has one in the tree; an app-level mount
     *  opened from elsewhere (a menu row) passes none. */
    children?: React.ReactNode;
  }>;

/**
 * The phone presentation on its own: `VisualPickerPanel` seated in a bottom
 * `Drawer`, driven by `open`. `VisualPicker` uses it in the compact tier; a
 * host whose picker is opened from somewhere that cannot hold a drawer (a
 * menu row, which unmounts with its menu) mounts it once at the app level
 * and opens it by state.
 */
export function VisualPickerSheet({
  open,
  onOpenChange,
  children,
  ...panel
}: VisualPickerSheetProps) {
  // A `half` drawer, the selector's own: it rests at the middle, a thumb
  // drags the icon grid's long scroll up to the top, and a tap on the search
  // lifts it there clear of the keyboard.
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      {children}
      {/* No `initialFocus` on the search: a sheet's open lands on the popup,
          and the keyboard waits for a tap on the field. */}
      <DrawerPopup showBar>
        <VisualPickerPanel
          {...panel}
          onDismiss={() => onOpenChange(false)}
          presentation="sheet"
        />
      </DrawerPopup>
    </Drawer>
  );
}

/**
 * The one picker popup for entity appearance: `VisualPickerPanel` inside its
 * own Popover, opened from the default tinted glyph tile or a caller-owned
 * trigger.
 */
export const VisualPicker = React.memo(function VisualPicker(
  props: VisualPickerProps,
) {
  const {
    trigger,
    size = "default",
    variant = "soft",
    disabled,
    open: openProp,
    onOpenChange,
  } = props;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  // A phone has no room to hang a 300px popup off a tile, and a thumb wants
  // the registry as a sheet it can scroll: the same panel, seated in a
  // bottom drawer instead of a popover.
  const isMobile = useIsMobile();

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );

  const hasColor = props.onColorChange !== undefined;
  const hasIcon = props.onIconChange !== undefined;
  // Narrowed alias for monogram mode — the letter renders in the default
  // trigger's glyph slot.
  const monogramProps = props.monogram !== undefined ? props : null;
  const monogramLetter =
    monogramProps !== null
      ? monogramProps.monogram.trim().charAt(0).toUpperCase() || "?"
      : null;
  const monogramActive = monogramProps !== null && monogramProps.icon === null;

  const tint: Color | undefined =
    hasColor && props.color !== "none" ? props.color : undefined;

  const defaultTooltip =
    monogramProps !== null ? "Set avatar" : hasIcon ? "Set icon" : "Set color";
  // A disabled picker gets no tooltip: TooltipTrigger consumes the `disabled`
  // prop (it keeps disabled triggers hoverable), which would strip the native
  // disabled state off the control it is wrapping.
  const tooltip = disabled
    ? null
    : props.tooltip === undefined
      ? trigger
        ? null
        : defaultTooltip
      : props.tooltip;

  // The TooltipTrigger sits INSIDE the popover trigger's render chain so its
  // hover handlers merge onto the trigger element itself; a caller-owned
  // trigger must spread unknown props onto its element for the same reason.
  const triggerRender =
    tooltip === null ? trigger : <TooltipTrigger render={trigger} />;

  const TriggerPart = isMobile ? DrawerTrigger : PopoverTrigger;

  const defaultTrigger = (
    <TriggerPart
      aria-label={tooltip ?? undefined}
      className={visualPickerTriggerVariants({
        size,
        tint: tint !== undefined,
        variant,
      })}
      data-color={tint}
      data-slot="visual-picker-trigger"
      disabled={disabled}
      render={triggerRender}
    >
      {monogramActive ? (
        <Text
          className={cn(
            visualPickerMonogramVariants({ size }),
            visualPickerTriggerInkVariants({
              tint: tint !== undefined,
              variant,
            }),
          )}
        >
          {monogramLetter}
        </Text>
      ) : (
        <Icon
          name={props.icon ?? "palette"}
          size={size === "sm" ? "lg" : "xl"}
          className={visualPickerTriggerInkVariants({
            tint: tint !== undefined,
            variant,
          })}
        />
      )}
    </TriggerPart>
  );

  const popoverTrigger = trigger ? (
    <TriggerPart
      data-slot="visual-picker-trigger"
      disabled={disabled}
      render={triggerRender}
    />
  ) : (
    defaultTrigger
  );

  const namedTrigger =
    tooltip === null ? (
      popoverTrigger
    ) : (
      <Tooltip>
        {popoverTrigger}
        <TooltipPopup>{tooltip}</TooltipPopup>
      </Tooltip>
    );

  if (isMobile) {
    const {
      trigger: _trigger,
      size: _size,
      variant: _variant,
      disabled: _disabled,
      tooltip: _tooltip,
      open: _open,
      onOpenChange: _onOpenChange,
      ...panel
    } = props;
    return (
      <VisualPickerSheet {...panel} onOpenChange={setOpen} open={open}>
        {namedTrigger}
      </VisualPickerSheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {namedTrigger}
      <PopoverPopup
        align="start"
        className="w-fit"
        // The search field is the first thing anyone reaches for in a grid
        // of several hundred glyphs, so it takes focus on open rather than
        // the popup.
        initialFocus={hasIcon ? searchRef : undefined}
        padding="none"
        side="bottom"
      >
        <VisualPickerPanel
          {...props}
          onDismiss={() => setOpen(false)}
          searchRef={searchRef}
        />
      </PopoverPopup>
    </Popover>
  );
});
